import React, { CSSProperties } from 'react';

import PropTypes from 'prop-types';
import BaseTable, { AutoResizer } from 'react-base-table';
import styled, { CSSProp } from 'styled-components';

import { Flex, Text } from '@adsk/alloy-react-box';
import { LinkButton } from '@adsk/alloy-react-button';
import Checkbox from '@adsk/alloy-react-checkbox';
import { omitKeys } from '@adsk/alloy-react-helpers';
import { AlertWarningIcon, CheckmarkIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import theme, { stylePropType } from '@adsk/alloy-react-theme';

import reactBaseTableCSS from './helpers/react-base-table-css';

const TableWrapper = styled.div`
  ${reactBaseTableCSS};
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow-y: hidden;
  & .TableRow:hover {
    cursor: pointer;
    background-color: ${theme.colors.adskBlue100};
  }
  & .TableRow--disabled:hover {
    cursor: not-allowed;
    background-color: ${theme.colors.charcoal100};
  }
`;

const TableFooter = ({
  loading,
  error,
  onRetry,
  loadingMoreText,
  errorText,
}: {
  loading?: boolean;
  error?: boolean;
  onRetry?: () => void;
  loadingMoreText?: React.ReactNode;
  errorText?: React.ReactNode;
}) => {
  const { T } = useLocale(({ locale }) => require(`./i18n/${locale}.json`));
  let footer = null;
  if (loading) {
    footer = (
      <>
        <ProgressRing size={ProgressRing.SIZES.SMALL} />
        <Text typography="bodySmall" color="charcoal700" marginLeft="13px">
          {loadingMoreText || T.translate('LOADING_MORE')}
        </Text>
      </>
    );
  } else if (error) {
    footer = (
      <>
        <AlertWarningIcon
          style={{
            color: theme.colors.charcoal700,
          }}
        />
        <Text
          typography="bodySmall"
          color="charcoal700"
          marginLeft="12px"
          marginRight="8px"
        >
          {errorText || T.translate('ERROR_LOADING_ALL_RESULTS')}
        </Text>
        <LinkButton onClick={onRetry} style={theme.typography.bodySmall}>
          {T.translate('TRY_AGAIN')}
        </LinkButton>
      </>
    );
  }

  return (
    <Flex
      alignItems="center"
      paddingLeft="50px"
      typography="bodySmall"
      width="100%"
      height="100%"
    >
      {footer}
    </Flex>
  );
};

TableFooter.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.bool,
  onRetry: PropTypes.func,
  loadingMoreText: PropTypes.string,
  errorText: PropTypes.string,
};

const SelectionCell = <T extends { id: string }>({
  rowData,
  onChange,
  disabledIds = [],
  selectedIds = [],
  multiple,
}: {
  rowData: T;
  onChange: ({ entity, checked }: { entity: T; checked?: boolean }) => void;
  disabledIds?: string[];
  selectedIds?: string[];
  multiple?: boolean;
}) => {
  if (!multiple) {
    return selectedIds.includes(rowData.id) ? (
      <CheckmarkIcon style={{ color: theme.colors.adskBlue700 }} />
    ) : null;
  }

  return (
    <Checkbox
      checked={selectedIds.includes(rowData.id)}
      id={rowData.id}
      disabled={disabledIds.includes(rowData.id)}
      onChange={() =>
        onChange({
          entity: rowData,
          checked: !selectedIds.includes(rowData.id),
        })
      }
    />
  );
};

SelectionCell.propTypes = {
  rowData: PropTypes.shape({
    id: PropTypes.string,
  }),
  onChange: PropTypes.func,
  disabledIds: PropTypes.array,
  selectedIds: PropTypes.array,
  multiple: PropTypes.bool,
};

const SelectionCellHeader = <T extends { id: string }>({
  entities,
  disabledIds = [],
  selectedIds = [],
  setSelection,
}: {
  entities: T[];
  disabledIds?: string[];
  selectedIds?: string[];
  setSelection: (selection: T[]) => void;
}) => {
  const indeterminate =
    entities.some((entity) => selectedIds.includes(entity.id)) &&
    selectedIds.length < entities.length;
  const allChecked = selectedIds.length >= entities.length;
  return (
    <Checkbox
      checked={indeterminate ? Checkbox.INDETERMINATE : allChecked}
      onChange={() =>
        setSelection(
          allChecked
            ? []
            : entities.filter(({ id }) => !disabledIds.includes(id)),
        )
      }
    />
  );
};

SelectionCellHeader.propTypes = {
  entities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
  ),
  selectedIds: PropTypes.arrayOf(PropTypes.string),
  disabledIds: PropTypes.arrayOf(PropTypes.string),
  setSelection: PropTypes.func,
};

const EntitiesTable = <T extends { id: string }>({
  className,
  style,
  disabledIds = [],
  selectedIds = [],
  entities = [],
  onLoadMore,
  multiple,
  loading,
  error,
  setSelection,
  onTryAgain,
  loadingMoreText,
  errorText,
  ...rest
}: {
  className?: string;
  style?: CSSProp;
  disabledIds?: string[];
  selectedIds?: string[];
  entities: T[];
  onLoadMore?: (() => void) | null;
  multiple?: boolean;
  loading?: boolean;
  error?: boolean;
  setSelection: (selection: T[]) => void;
  onTryAgain: () => void;
  loadingMoreText?: React.ReactNode;
  errorText?: React.ReactNode;
  [key: string]: unknown;
}) => {
  const selectedRowKeys = selectedIds;

  const tableRowProps = !multiple && {
    rowEventHandlers: {
      onClick: ({ rowData }: { rowData: T }) => {
        if (!disabledIds.includes(rowData.id)) {
          setSelection(selectedIds.includes(rowData.id) ? [] : [rowData]);
        }
      },
    },
    rowClassName: ({ rowData }: { rowData: T }) =>
      disabledIds.includes(rowData.id) ? 'TableRow--disabled' : 'TableRow',
  };
  const tableFooter = (
    <TableFooter
      loading={loading}
      error={error}
      onRetry={onTryAgain}
      loadingMoreText={loadingMoreText}
      errorText={errorText}
    />
  );
  return (
    <TableWrapper className={className} style={style as CSSProperties}>
      <AutoResizer>
        {({ width = 1000, height = 600 }) => (
          <BaseTable
            width={width}
            height={height}
            data={entities}
            selectedRowKeys={selectedRowKeys}
            footerRenderer={tableFooter}
            footerHeight={(loading || error) && entities.length > 0 ? 50 : 0}
            onEndReachedThreshold={300}
            onEndReached={() => {
              if (!onLoadMore || ((error || loading) && entities.length > 0)) {
                return;
              }
              onLoadMore();
            }}
            {...tableRowProps}
            {...rest}
          />
        )}
      </AutoResizer>
    </TableWrapper>
  );
};

EntitiesTable.SelectionCell = SelectionCell;
EntitiesTable.SelectionCellHeader = SelectionCellHeader;

EntitiesTable.propTypes = {
  ...omitKeys(BaseTable.propTypes, [
    'data',
    'width',
    'headerHeight',
    'rowKey',
    'rowHeight',
  ]),
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Array of disabled IDs */
  disabledIds: PropTypes.arrayOf(PropTypes.string),
  /** Array of selected IDs */
  selectedIds: PropTypes.arrayOf(PropTypes.string),
  /** Array of entities */
  entities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    }),
  ),
  /** Whether to use multi select or not */
  multiple: PropTypes.bool,
  /** On load more handler */
  onLoadMore: PropTypes.func,
  /** On selection change handler. Shape: ({ entity, checked }) => * */
  onChange: PropTypes.func,
  /** Loading state */
  loading: PropTypes.bool,
  /** Error state */
  error: PropTypes.bool,
  /** Try again handler */
  onTryAgain: PropTypes.func,
  /** Set selection hand */
  setSelection: PropTypes.func,
  /** Loading more text */
  loadingMoreText: PropTypes.string,
  /** Error text */
  errorText: PropTypes.string,
};

export default EntitiesTable;
